<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import YoutubePlayer from './YoutubePlayer.vue';

const VIDEO_ID = 'dlQlcwgGk6A';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

// Tracks if the user closed the modal during this session (without "don't show again")
const closedThisSession = ref(false);
const dontShowAgain = ref(false);

// Show when: not permanently dismissed AND not closed this session
const isVisible = computed(
  () =>
    uiSettings.value?.welcome_video_dismissed !== true &&
    !closedThisSession.value
);

const close = () => {
  if (dontShowAgain.value) {
    updateUISettings({ welcome_video_dismissed: true });
  }
  closedThisSession.value = true;
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
    >
      <div
        class="relative w-full max-w-3xl bg-white dark:bg-n-slate-2 rounded-2xl shadow-2xl overflow-hidden"
      >
        <!-- Close button (top-right) -->
        <button
          class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-n-slate-11 hover:bg-n-slate-3 transition-colors"
          @click="close"
        >
          <i class="i-lucide-x w-4 h-4" />
        </button>

        <!-- Header -->
        <div class="px-8 pt-8 pb-5 text-center">
          <div
            class="inline-flex items-center justify-center w-12 h-12 bg-n-brand/10 rounded-full mb-4"
          >
            <i class="i-lucide-play-circle w-6 h-6 text-n-brand" />
          </div>
          <h1 class="text-xl font-bold text-n-slate-12">
            {{ t('WELCOME_VIDEO.TITLE') }}
          </h1>
          <p class="mt-1.5 text-sm text-n-slate-11">
            {{ t('WELCOME_VIDEO.SUBTITLE') }}
          </p>
        </div>

        <!-- Video player -->
        <div class="px-8 pb-5">
          <YoutubePlayer :video-id="VIDEO_ID" />
        </div>

        <!-- Footer: checkbox + close button -->
        <div
          class="px-8 pb-7 flex items-center justify-between gap-4 flex-wrap"
        >
          <label
            class="flex items-center gap-2 cursor-pointer select-none group"
          >
            <input
              v-model="dontShowAgain"
              type="checkbox"
              class="w-4 h-4 rounded cursor-pointer accent-n-brand"
            />
            <span
              class="text-sm text-n-slate-11 group-hover:text-n-slate-12 transition-colors"
            >
              {{ t('WELCOME_VIDEO.DONT_SHOW_AGAIN') }}
            </span>
          </label>

          <button
            class="px-5 py-2 rounded-lg bg-n-brand text-white text-sm font-medium hover:opacity-90 transition-opacity"
            @click="close"
          >
            {{ t('WELCOME_VIDEO.CLOSE') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
